// packages/frontend/src/utils/telegram.js

/**
 * Telegram WebApp helpers (Telegram is a secondary signal; AgeVerif stays primary).
 * Monorepo-safe: no hardcoded origins. All fetches use same-origin + cookies.
 */

function tg() {
  return typeof window !== 'undefined' ? window?.Telegram?.WebApp : undefined;
}

export function isTelegram() {
  return Boolean(tg());
}

export function getInitData() {
  const api = tg();
  if (!api) return { raw: '', unsafe: {}, user: undefined };
  const raw = api.initData || '';
  const unsafe = api.initDataUnsafe || {};
  return { raw, unsafe, user: unsafe?.user };
}

/**
 * Optional DOM theme sync helper.
 * If you prefer to keep utils headless, do NOT call this directly:
 * use the useSyncTelegramTheme() hook instead.
 */
export function setHtmlDataThemeFromTelegram(colorScheme) {
  const el = typeof document !== 'undefined' ? document.documentElement : null;
  if (!el) return;
  if (colorScheme === 'dark') el.setAttribute('data-theme', 'dark');
  else if (colorScheme === 'light') el.setAttribute('data-theme', 'light');
  else el.removeAttribute('data-theme'); // fall back to prefers-color-scheme
}

/**
 * Initialize Telegram WebApp (idempotent).
 * - Calls WebApp.ready()
 * - Optionally expand main view (Telegram-safe)
 * - Optionally request fullscreen (Mini Apps 2.0, if available)
 * - Subscribes to theme changes and invokes onThemeChange(themeParams, colorScheme)
 *
 * NOTE: This function does not manipulate DOM unless you keep the default
 * onThemeChange handler, which calls setHtmlDataThemeFromTelegram().
 */
export function initTelegram({
  expand = true,
  requestFullscreen = true,
  onThemeChange = (_themeParams, colorScheme) => {
    // Default: update <html data-theme="..."> so your CSS can react
    setHtmlDataThemeFromTelegram(colorScheme);
  },
} = {}) {
  const api = tg();
  if (!api) return { inTelegram: false };

  try {
    if (!api.__app_initialized) {
      api.ready?.();
      if (expand) api.expand?.();
      if (requestFullscreen) api.requestFullscreen?.();

      if (typeof onThemeChange === 'function') {
        // Initial call with current theme
        try {
          onThemeChange(api.themeParams || {}, api.colorScheme);
        } catch (_) {}

        // Subscribe to theme changes
        api.onEvent?.('themeChanged', () => {
          const themeParams = api.themeParams || {};
          const colorScheme = api.colorScheme; // "light" | "dark"
          try {
            onThemeChange(themeParams, colorScheme);
          } catch (_) {}
        });
      }

      api.__app_initialized = true;
    }

    return {
      inTelegram: true,
      colorScheme: api.colorScheme, // "light" | "dark"
      themeParams: api.themeParams || {},
      platform: api.platform,       // "ios" | "android" | "web"
      version: api.version,
    };
  } catch (e) {
    // Don't break app if Telegram object misbehaves
    console.warn('[telegram] init failed:', e);
    return { inTelegram: true };
  }
}

/**
 * Sync Telegram session to backend.
 * Sends raw initData to /api/telegram/sync so the server can verify HMAC and
 * associate the verified telegramId with the current AgeVerif session.
 *
 * Server must:
 *  - Verify initData signature using bot token (HMAC-SHA256).
 *  - Bind the verified telegramId to the current cookie session user.
 */
export async function syncTelegramSession() {
  const { raw } = getInitData();
  if (!raw) {
    // Not in Telegram or no initData available
    return { ok: false, reason: 'no_init_data' };
  }

  try {
    const res = await fetch('/api/telegram/sync', {
      method: 'POST',
      credentials: 'include', // use cookies; client never asserts identity
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData: raw }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      return { ok: false, status: res.status, body: text || undefined };
    }
    const data = await res.json().catch(() => ({}));
    return { ok: true, data };
  } catch (e) {
    console.warn('[telegram] sync failed:', e);
    return { ok: false, error: String(e) };
  }
}

/**
 * Haptics (no-ops outside Telegram).
 * pattern: "impact" | "notification" | "selection"
 * style:   "light" | "medium" | "heavy" (impact), "success" | "warning" | "error" (notification)
 */
export function haptic(pattern = 'impact', style = 'light') {
  const api = tg();
  const hf = api?.HapticFeedback;
  if (!hf) return;
  try {
    if (pattern === 'impact') hf.impactOccurred?.(style);
    else if (pattern === 'notification') hf.notificationOccurred?.(style);
    else if (pattern === 'selection') hf.selectionChanged?.();
  } catch {
    /* ignore */
  }
}

/**
 * Telegram-safe navigation.
 * - For same-origin URLs: use location.href so we stay inside the Mini App view.
 * - For external URLs (if you must): use WebApp.openLink; fallback to location.href.
 */
export function navigateInApp(url) {
  try {
    const target = new URL(url, window.location.origin);
    if (target.origin === window.location.origin) {
      // Same-origin → stay inside Telegram's webview
      window.location.href = target.pathname + target.search + target.hash;
      return;
    }
    // External → openLink (may open external browser on iOS; Mini App remains open behind)
    if (tg()?.openLink) {
      tg().openLink(target.toString());
    } else {
      window.location.href = target.toString();
    }
  } catch {
    window.location.href = url;
  }
}

/**
 * Guard to enforce "AgeVerif first".
 * Use this in UI actions that would otherwise start payment.
 */
export function requireAgeVerifFirst({ isAgeVerified, onFail } = {}) {
  if (!isAgeVerified) {
    if (typeof onFail === 'function') onFail();
    // optional tactile feedback
    haptic('notification', 'warning');
    return false;
  }
  return true;
}

/**
 * Convenience: initialize + optionally sync immediately.
 * Returns { init, sync, initData }.
 */
export async function bootstrapTelegram({
  expand = true,
  requestFullscreen = true,
  onThemeChange,      // if omitted, default onThemeChange updates <html data-theme="...">
  autoSync = true,
} = {}) {
  const init = initTelegram({ expand, requestFullscreen, onThemeChange });
  let sync = { ok: false, reason: 'skipped' };
  if (autoSync && isTelegram()) {
    sync = await syncTelegramSession();
  }
  return { init, sync, initData: getInitData() };
}
